import { useState, useCallback } from 'react';
import { Plus, Play, Loader2, CheckCircle2, XCircle } from 'lucide-react';
import Layout from '../components/layout/Layout';
import Header from '../components/layout/Header';
import {
    useSyncSources,
    useCreateSyncSource,
    useToggleSyncSource,
    useRunSync,
} from '../hooks/useSyncSources';
import type { SyncSourceCreatePayload, SyncResult } from '@shared/types/sync';

function formatDateTime(value: string | null): string {
    if (!value) return '-';
    const d = new Date(value);
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    const hh = String(d.getHours()).padStart(2, '0');
    const mi = String(d.getMinutes()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd} ${hh}:${mi}`;
}

const inputClass =
    'w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition';

const INITIAL_FORM: SyncSourceCreatePayload = {
    name: '',
    host: '',
    port: 3306,
    database_name: '',
    username: '',
    password: '',
    use_ssh_tunnel: false,
    ssh_host: '',
    ssh_port: 22,
    ssh_user: '',
    ssh_key_path: '',
};

export default function SettingsPage() {
    const [showForm, setShowForm] = useState(false);
    const [form, setForm] = useState<SyncSourceCreatePayload>({ ...INITIAL_FORM });
    const [syncResults, setSyncResults] = useState<SyncResult[] | null>(null);

    const { data: sources = [], isLoading } = useSyncSources();
    const createSource = useCreateSyncSource();
    const toggleSource = useToggleSyncSource();
    const runSync = useRunSync();

    const handleFieldChange = useCallback(
        (field: keyof SyncSourceCreatePayload, value: string | number | boolean | null) => {
            setForm((prev) => ({ ...prev, [field]: value }));
        },
        [],
    );

    const handleSubmit = useCallback(() => {
        if (!form.name || !form.host || !form.database_name || !form.username || !form.password) {
            return;
        }
        const payload: SyncSourceCreatePayload = {
            ...form,
            ssh_host: form.use_ssh_tunnel ? form.ssh_host || null : null,
            ssh_user: form.use_ssh_tunnel ? form.ssh_user || null : null,
            ssh_key_path: form.use_ssh_tunnel ? form.ssh_key_path || null : null,
        };
        createSource.mutate(payload, {
            onSuccess: () => {
                setForm({ ...INITIAL_FORM });
                setShowForm(false);
            },
        });
    }, [form, createSource]);

    const handleRunSync = useCallback(() => {
        setSyncResults(null);
        runSync.mutate(undefined, {
            onSuccess: (results) => {
                setSyncResults(results);
            },
            onError: () => {
                setSyncResults([]);
            },
        });
    }, [runSync]);

    return (
        <Layout>
            <Header
                title="설정"
                subtitle="동기화 소스 및 시스템 설정을 관리합니다"
                actions={
                    <button
                        onClick={handleRunSync}
                        disabled={runSync.isPending}
                        className="inline-flex items-center gap-1.5 px-3.5 py-2 text-xs font-medium bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition shadow-sm shadow-blue-200 disabled:opacity-50"
                    >
                        {runSync.isPending ? (
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        ) : (
                            <Play className="w-3.5 h-3.5" />
                        )}
                        {runSync.isPending ? '동기화 중...' : '수동 동기화 실행'}
                    </button>
                }
            />

            <div className="px-8 py-6 space-y-6">
                {/* 동기화 결과 */}
                {syncResults !== null && (
                    <SyncResultsCard results={syncResults} onClose={() => setSyncResults(null)} />
                )}

                {/* 동기화 소스 목록 */}
                <div className="bg-white rounded-xl border border-gray-100 shadow-sm">
                    <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
                        <h2 className="text-sm font-bold text-gray-900">동기화 소스</h2>
                        <button
                            onClick={() => setShowForm(!showForm)}
                            className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium border border-gray-300 rounded-lg hover:bg-gray-50 transition"
                        >
                            <Plus className="w-3.5 h-3.5" />
                            소스 추가
                        </button>
                    </div>

                    {/* 소스 추가 폼 */}
                    {showForm && (
                        <AddSourceForm
                            form={form}
                            onChange={handleFieldChange}
                            onSubmit={handleSubmit}
                            onCancel={() => { setShowForm(false); setForm({ ...INITIAL_FORM }); }}
                            isPending={createSource.isPending}
                        />
                    )}

                    {/* 소스 테이블 */}
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="border-b border-gray-100 text-left">
                                    <th className="px-6 py-3 text-xs font-semibold text-gray-500">소스명</th>
                                    <th className="px-6 py-3 text-xs font-semibold text-gray-500">호스트</th>
                                    <th className="px-6 py-3 text-xs font-semibold text-gray-500">DB명</th>
                                    <th className="px-6 py-3 text-xs font-semibold text-gray-500">활성</th>
                                    <th className="px-6 py-3 text-xs font-semibold text-gray-500">마지막 동기화</th>
                                </tr>
                            </thead>
                            <tbody>
                                {isLoading ? (
                                    <tr>
                                        <td colSpan={5} className="px-6 py-8 text-center text-gray-400 text-xs">
                                            불러오는 중...
                                        </td>
                                    </tr>
                                ) : sources.length === 0 ? (
                                    <tr>
                                        <td colSpan={5} className="px-6 py-8 text-center text-gray-400 text-xs">
                                            등록된 동기화 소스가 없습니다.
                                        </td>
                                    </tr>
                                ) : (
                                    sources.map((source) => (
                                        <tr key={source.id} className="border-b border-gray-50 hover:bg-gray-50/50 transition">
                                            <td className="px-6 py-3 font-medium text-gray-900">{source.name}</td>
                                            <td className="px-6 py-3 text-gray-600">{source.host}</td>
                                            <td className="px-6 py-3 text-gray-600">{source.database_name}</td>
                                            <td className="px-6 py-3">
                                                <ToggleSwitch
                                                    checked={source.is_active}
                                                    onChange={() => toggleSource.mutate(source.id)}
                                                    disabled={toggleSource.isPending}
                                                />
                                            </td>
                                            <td className="px-6 py-3 text-gray-500">
                                                {formatDateTime(source.last_synced_at)}
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </Layout>
    );
}

// ─── Toggle Switch ───
function ToggleSwitch({
    checked,
    onChange,
    disabled,
}: {
    checked: boolean;
    onChange: () => void;
    disabled?: boolean;
}) {
    return (
        <button
            type="button"
            role="switch"
            aria-checked={checked}
            onClick={onChange}
            disabled={disabled}
            className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${checked ? 'bg-blue-500' : 'bg-gray-300'
                } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
        >
            <span
                className={`inline-block h-3.5 w-3.5 rounded-full bg-white shadow transition-transform ${checked ? 'translate-x-4' : 'translate-x-0.5'
                    }`}
            />
        </button>
    );
}

// ─── Add Source Form ───
function AddSourceForm({
    form,
    onChange,
    onSubmit,
    onCancel,
    isPending,
}: {
    form: SyncSourceCreatePayload;
    onChange: (field: keyof SyncSourceCreatePayload, value: string | number | boolean | null) => void;
    onSubmit: () => void;
    onCancel: () => void;
    isPending: boolean;
}) {
    return (
        <div className="px-6 py-5 border-b border-gray-100 bg-gray-50/50">
            <div className="grid grid-cols-3 gap-4 mb-4">
                <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">소스명 *</label>
                    <input
                        type="text"
                        value={form.name}
                        onChange={(e) => onChange('name', e.target.value)}
                        placeholder="예: mytown-prod"
                        className={inputClass}
                    />
                </div>
                <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">호스트 *</label>
                    <input
                        type="text"
                        value={form.host}
                        onChange={(e) => onChange('host', e.target.value)}
                        placeholder="예: rds-endpoint.amazonaws.com"
                        className={inputClass}
                    />
                </div>
                <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">포트</label>
                    <input
                        type="number"
                        value={form.port ?? 3306}
                        onChange={(e) => onChange('port', Number(e.target.value))}
                        className={inputClass}
                    />
                </div>
            </div>
            <div className="grid grid-cols-3 gap-4 mb-4">
                <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">DB명 *</label>
                    <input
                        type="text"
                        value={form.database_name}
                        onChange={(e) => onChange('database_name', e.target.value)}
                        placeholder="예: mytown"
                        className={inputClass}
                    />
                </div>
                <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">접속 계정 *</label>
                    <input
                        type="text"
                        value={form.username}
                        onChange={(e) => onChange('username', e.target.value)}
                        placeholder="예: mytownro"
                        className={inputClass}
                    />
                </div>
                <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">비밀번호 *</label>
                    <input
                        type="password"
                        value={form.password}
                        onChange={(e) => onChange('password', e.target.value)}
                        className={inputClass}
                    />
                </div>
            </div>

            {/* SSH 터널 설정 */}
            <div className="mb-4">
                <label className="inline-flex items-center gap-2 text-xs font-medium text-gray-600 cursor-pointer">
                    <input
                        type="checkbox"
                        checked={form.use_ssh_tunnel ?? false}
                        onChange={(e) => onChange('use_ssh_tunnel', e.target.checked)}
                        className="rounded border-gray-300 text-blue-500 focus:ring-blue-500/20"
                    />
                    SSH 터널 사용
                </label>
            </div>

            {form.use_ssh_tunnel && (
                <div className="grid grid-cols-4 gap-4 mb-4">
                    <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">SSH 호스트</label>
                        <input
                            type="text"
                            value={form.ssh_host ?? ''}
                            onChange={(e) => onChange('ssh_host', e.target.value)}
                            className={inputClass}
                        />
                    </div>
                    <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">SSH 포트</label>
                        <input
                            type="number"
                            value={form.ssh_port ?? 22}
                            onChange={(e) => onChange('ssh_port', Number(e.target.value))}
                            className={inputClass}
                        />
                    </div>
                    <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">SSH 사용자</label>
                        <input
                            type="text"
                            value={form.ssh_user ?? ''}
                            onChange={(e) => onChange('ssh_user', e.target.value)}
                            className={inputClass}
                        />
                    </div>
                    <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">SSH 키 경로</label>
                        <input
                            type="text"
                            value={form.ssh_key_path ?? ''}
                            onChange={(e) => onChange('ssh_key_path', e.target.value)}
                            placeholder="/path/to/key.pem"
                            className={inputClass}
                        />
                    </div>
                </div>
            )}

            <div className="flex items-center gap-2">
                <button
                    onClick={onSubmit}
                    disabled={isPending}
                    className="px-4 py-2 text-xs font-medium bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition shadow-sm shadow-blue-200 disabled:opacity-50"
                >
                    {isPending ? '저장 중...' : '소스 추가'}
                </button>
                <button
                    onClick={onCancel}
                    className="px-4 py-2 text-xs font-medium border border-gray-200 rounded-lg hover:bg-gray-50 transition"
                >
                    취소
                </button>
            </div>
        </div>
    );
}

// ─── Sync Results Card ───
function SyncResultsCard({
    results,
    onClose,
}: {
    results: SyncResult[];
    onClose: () => void;
}) {
    const allSuccess = results.length > 0 && results.every((r) => r.success);
    const hasErrors = results.some((r) => !r.success);

    return (
        <div
            className={`rounded-xl border shadow-sm p-5 ${allSuccess
                    ? 'bg-green-50 border-green-200'
                    : hasErrors
                        ? 'bg-yellow-50 border-yellow-200'
                        : 'bg-gray-50 border-gray-200'
                }`}
        >
            <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-bold text-gray-900">동기화 결과</h3>
                <button
                    onClick={onClose}
                    className="text-xs text-gray-400 hover:text-gray-600 transition"
                >
                    닫기
                </button>
            </div>
            {results.length === 0 ? (
                <p className="text-xs text-gray-500">동기화 결과가 없습니다.</p>
            ) : (
                <div className="space-y-2">
                    {results.map((r) => (
                        <div
                            key={r.sourceId}
                            className="flex items-center gap-3 text-xs"
                        >
                            {r.success ? (
                                <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" />
                            ) : (
                                <XCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
                            )}
                            <span className="font-medium text-gray-700">{r.sourceName}</span>
                            {r.success ? (
                                <span className="text-gray-500">
                                    신규 {r.inserted}건, 업데이트 {r.updated}건
                                    <span className="text-gray-400 ml-1">({r.duration}ms)</span>
                                </span>
                            ) : (
                                <span className="text-red-600">{r.error}</span>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
